// BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-07 — top-level Footer with Legal nav surface
// Mounted at App.jsx top level so it renders on every route per BR-GAP-012.
// 7 link targets: 6 routed /legal/* pages + /.well-known/security.txt.
//
// PROP-HOMEPAGE-SIGNUP-READINESS-001 P2 (footer consolidation, option b):
// This Footer.jsx is the CANONICAL single legal+social footer presented to anon
// visitors. The Maestro homepage's duplicate <MaestroFooter /> mount was removed
// (MaestroHomepage.jsx) so the homepage now renders exactly ONE footer — this one.
// MaestroFooter is retained as a SOCIAL-ONLY bar for the Supply Lab pages
// (SupplyLab / SupplyLabProduct still mount it; those are outside this proposal's
// scope) — its dead legal array + Twitter were stripped so NO href="#" remains in
// any footer on the platform.
// Decision rationale: full single-footer deletion (option a) cascaded into the two
// Supply Lab call sites; option (b) keeps the change atomic to the homepage funnel.
//
// PROP-HOMEPAGE-SIGNUP-READINESS-001 P1 (social rewire):
// Social icons link to the operator's live pages (Facebook / Instagram / YouTube;
// NO Twitter/X), opening in a new tab with rel="noopener noreferrer". URLs are the
// operator-ratified live values; no href="#" placeholders.

import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube } from "lucide-react";
import SUPPORT_CONFIG from "../config/support.config";

const LEGAL_LINKS = [
  { href: "/legal/terms", label: "Terms" },
  { href: "/legal/privacy", label: "Privacy" },
  { href: "/legal/cookies", label: "Cookies" },
  { href: "/legal/aup", label: "Acceptable Use" },
  { href: "/legal/dmca", label: "DMCA" },
  { href: "/legal/dpa", label: "DPA" },
];

// PROP-HOMEPAGE-SIGNUP-READINESS-001 P1 + BR-GAP-004 (single social source of truth):
// Derived from SUPPORT_CONFIG.social — the ONE canonical social config, corrected to the
// operator's live Facebook/Instagram/YouTube pages (no Twitter/X). No second/divergent
// source and no href="#" placeholder. Stable display order + labels via the maps below.
const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube };
const SOCIAL_LABELS = { facebook: "Facebook", instagram: "Instagram", youtube: "YouTube" };
const SOCIAL_ORDER = ["facebook", "instagram", "youtube"];
const SOCIAL_LINKS = SOCIAL_ORDER
  .filter((key) => SUPPORT_CONFIG.social && SUPPORT_CONFIG.social[key])
  .map((key) => ({ label: SOCIAL_LABELS[key], href: SUPPORT_CONFIG.social[key], Icon: SOCIAL_ICONS[key] }));

export default function Footer({ onOpenCookiePreferences }) {
  return (
    <footer className="border-t border-zinc-800 bg-zinc-950 py-8 px-6 mt-auto">
      <div className="mx-auto max-w-6xl">
        <nav aria-label="Legal" className="flex flex-wrap gap-x-6 gap-y-2 justify-center">
          {LEGAL_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="text-sm text-zinc-400 hover:text-orange-500 transition-colors"
            >
              {link.label}
            </Link>
          ))}
          {/* /.well-known/security.txt is a static-served plain-text file — anchor tag, not Router Link */}
          <a
            href="/.well-known/security.txt"
            className="text-sm text-zinc-400 hover:text-orange-500 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
          >
            Security
          </a>
          {/* Cookie Preferences — re-opens klaro banner per BR-GAP-001 withdrawal mechanism */}
          {onOpenCookiePreferences && (
            <button
              type="button"
              data-testid="cookie-preferences-link"
              onClick={onOpenCookiePreferences}
              className="text-sm text-zinc-400 hover:text-orange-500 transition-colors bg-transparent border-0 cursor-pointer p-0"
            >
              Cookie Preferences
            </button>
          )}
        </nav>
        {/* PROP-HOMEPAGE-SIGNUP-READINESS-001 P1: rewired social icons (live pages, new tab) */}
        <nav
          aria-label="Social media"
          className="flex gap-x-6 justify-center mt-4"
        >
          {SOCIAL_LINKS.map(({ label, href, Icon }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className="text-zinc-400 hover:text-orange-500 transition-colors inline-flex items-center justify-center"
            >
              <Icon size={18} aria-hidden="true" />
            </a>
          ))}
        </nav>
        <p className="text-xs text-zinc-600 text-center mt-4">
          © {new Date().getFullYear()} Muñoz Digital Media, LLC · maestrograding.ai
        </p>
      </div>
    </footer>
  );
}
